import { useEffect, useState } from "react";
import { usePageMeta } from "../seo";
import { Play } from "lucide-react";
import { CtaBand } from "../components/BookCta";
import { Reveal } from "../components/motion";
import { IMAGES, OCCASIONS, MEDIA } from "../data/site";

const GROUPS = [
    {
        key: "proposals",
        label: "Proposals",
        media: [
            { type: "video", src: MEDIA.heroVideo, poster: "/media/hero-poster.jpg" },
            { type: "image", src: "/media/proposal.jpg" },
            { type: "image", src: MEDIA.tent },
        ],
    },
    {
        key: "showers",
        label: "Bridal Showers",
        media: [
            { type: "video", src: MEDIA.clip4, poster: "/media/clip4-poster.jpg" },
            { type: "image", src: "/media/blush-tent.jpg" },
            { type: "image", src: "/media/adriatica.jpg" },
        ],
    },
    {
        key: "birthdays",
        label: "Birthdays",
        media: [
            { type: "video", src: "/media/birthday-film.mp4", poster: "/media/birthday-film-poster.jpg" },
            { type: "image", src: "/media/movie-night.jpg" },
            { type: "image", src: "/media/sunset-table.jpg" },
        ],
    },
    {
        key: "date-nights",
        label: "Date Nights",
        media: [
            { type: "video", src: MEDIA.clip1, poster: "/media/clip1-poster.jpg" },
            { type: "video", src: MEDIA.clip2, poster: "/media/clip2-poster.jpg" },
            { type: "image", src: "/media/sunset-table.jpg" },
        ],
    },
    {
        key: "anniversaries",
        label: "Anniversaries",
        media: [
            { type: "image", src: "/media/anniversary.jpg" },
            { type: "video", src: MEDIA.clip2, poster: "/media/clip2-poster.jpg" },
            { type: "video", src: MEDIA.clip1, poster: "/media/clip1-poster.jpg" },
        ],
    },
];

export default function Gallery() {
    const [filter, setFilter] = useState("all");

    usePageMeta(
        "Gallery | Proposal, Shower & Date Night Styling in McKinney & DFW",
        "Real photos and films of luxury proposal setups, bridal showers, birthdays, date nights and anniversaries styled by Enchanting Escapes across McKinney and Dallas-Fort Worth."
    );

    useEffect(() => {
        const hash = window.location.hash.replace("#", "");
        if (GROUPS.some((g) => g.key === hash)) setFilter(hash);
    }, []);

    const visible = filter === "all" ? GROUPS : GROUPS.filter((g) => g.key === filter);

    return (
        <main data-testid="gallery-page">
            <section className="py-20 sm:py-28">
                <div className="max-w-6xl mx-auto px-5 sm:px-8">
                    <Reveal className="max-w-2xl mb-12">
                        <p className="font-accent text-xs uppercase tracking-[0.25em] text-[#C18E8B] mb-4">
                            The Gallery
                        </p>
                        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#2D230A] leading-tight">
                            Styled escapes across McKinney & Dallas-Fort Worth
                        </h1>
                        <p className="mt-5 text-[#6E5B3D] leading-relaxed">
                            Browse our recent luxury proposal setups in McKinney, bridal
                            shower styling in DFW, birthday picnic setups and outdoor date
                            nights in Dallas — real photos and films from our own events,
                            built on floor cushions, low tables and candlelight.
                        </p>
                    </Reveal>

                    <Reveal delay={0.1} className="flex flex-wrap gap-3 mb-14" testid="gallery-filters">
                        {[{ key: "all", label: "All Occasions" }, ...GROUPS.map(({ key, label }) => ({ key, label }))].map((f) => (
                            <button
                                key={f.key}
                                data-testid={`gallery-filter-${f.key}`}
                                onClick={() => setFilter(f.key)}
                                className={`px-5 py-2 rounded-full font-accent text-xs uppercase tracking-[0.15em] border transition-all duration-300 ${
                                    filter === f.key
                                        ? "bg-[#554011] text-[#FAF7F2] border-[#554011] scale-105"
                                        : "bg-white text-[#6E5B3D] border-[#E8C4C1] hover:border-[#C18E8B] hover:text-[#554011]"
                                }`}
                            >
                                {f.label}
                            </button>
                        ))}
                    </Reveal>

                    <div className="space-y-20">
                        {visible.map((group, gi) => {
                            const meta = OCCASIONS.find((o) => o.key === group.key);
                            return (
                                <section key={group.key} id={group.key} data-testid={`gallery-group-${group.key}`}>
                                    <Reveal>
                                        <h2 className="font-display text-2xl sm:text-3xl text-[#554011] mb-2">
                                            {meta?.title || group.label}
                                        </h2>
                                        <p className="text-[#6E5B3D] mb-7 max-w-2xl">{meta?.blurb}</p>
                                    </Reveal>
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5">
                                        {group.media.map((m, i) => (
                                            <Reveal key={i} delay={i * 0.12}>
                                                <div
                                                    data-testid={`gallery-image-${group.key}-${i}`}
                                                    className={`group relative overflow-hidden aspect-[4/5] shadow-sm hover:shadow-xl transition-shadow duration-500 ${
                                                        i === 0 ? "arch-frame" : "rounded-2xl"
                                                    } ${gi % 2 === 1 && i === 1 ? "sm:mt-10" : ""}`}
                                                >
                                                    {m.type === "video" ? (
                                                        <>
                                                            <video
                                                                src={m.src}
                                                                poster={m.poster}
                                                                autoPlay
                                                                muted
                                                                loop
                                                                playsInline
                                                                className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.06] transition-transform duration-[1100ms] ease-[cubic-bezier(0.22,1,0.36,1)]"
                                                            />
                                                            <span className="absolute bottom-4 left-4 flex items-center gap-1.5 bg-[#2D230A]/60 backdrop-blur-sm text-[#FAF7F2] px-3 py-1.5 rounded-full font-accent text-[10px] uppercase tracking-[0.15em]">
                                                                <Play className="w-3 h-3 fill-current" /> Film
                                                            </span>
                                                        </>
                                                    ) : (
                                                        <img
                                                            src={m.src}
                                                            alt={`${group.label} styling by Enchanting Escapes in the DFW area — photo ${i + 1}`}
                                                            loading="lazy"
                                                            className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.06] transition-transform duration-[1100ms] ease-[cubic-bezier(0.22,1,0.36,1)]"
                                                        />
                                                    )}
                                                </div>
                                            </Reveal>
                                        ))}
                                    </div>
                                </section>
                            );
                        })}
                    </div>
                </div>
            </section>

            <CtaBand
                heading="Love what you see?"
                subtext="Every setup in this gallery started with a simple inquiry. Tell us your occasion and we'll design yours."
            />
        </main>
    );
}
